import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { EyeOff, Flag, Layers, Lock, Power, ShieldBan } from 'lucide-react';
import {
  aendereKategorie,
  aendereThema,
  blendeBeitragAus,
  erledigeMeldung,
  hebeSperreAuf,
  ladeKategorien,
  ladeMeldungen,
  ladeSperren,
  ladeStatus,
  neueKategorie,
  schalteForum,
  sperre as setzeSperre,
  type ForumMeldung,
} from '@/api/forum.api';
import { useConfirm } from '@/components/common/ConfirmDialog';

// ─── Das Forum betreiben ─────────────────────────────────────────────────────
//
// Vier Dinge, und mehr braucht es in Etappe 1 nicht: an/aus, Bereiche,
// Meldungen, Schreibsperren.
//
// Die Meldungen stehen ganz oben und mit der offenen Zahl im Reiter. Alles
// andere kann warten; eine unbearbeitete Meldung ist der einzige Punkt, an dem
// jemand auf den Betrieb wartet.

const GRUND_LABELS: Record<string, string> = {
  spam: 'Werbung',
  beleidigung: 'Beleidigend',
  personendaten: 'Daten Dritter',
  sonstiges: 'Sonstiges',
};

function zeit(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function tage(bis: string): number {
  return Math.max(0, Math.ceil((new Date(bis).getTime() - Date.now()) / 86_400_000));
}

// ─── Schalter ────────────────────────────────────────────────────────────────

function Schalter() {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { data } = useQuery({ queryKey: ['admin', 'forum', 'status'], queryFn: ladeStatus });

  const schalten = useMutation({
    mutationFn: (an: boolean) => schalteForum(an),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'forum'] });
      toast.success(r.an ? 'Das Forum ist an.' : 'Das Forum ist aus.');
    },
    onError: (err: Error) => toast.error(err.message || 'Das ging nicht.'),
  });

  if (!data) return null;

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="flex items-center gap-2 text-base font-semibold text-slate-800">
            <Power className="h-4 w-4 text-slate-400" aria-hidden="true" />
            Forum {data.an ? 'ist an' : 'ist aus'}
          </h2>
          <p className="mt-1 max-w-xl text-sm text-slate-500">
            {data.an
              ? 'Konten ab dem Tarif, der die Gemeinschaft enthält, sehen den Menüpunkt und ' +
                'können lesen und schreiben.'
              : 'Niemand sieht das Forum – auch die vorhandenen Beiträge nicht. Sie bleiben ' +
                'erhalten und sind nach dem Einschalten wieder da.'}
          </p>
        </div>
        <button
          onClick={async () => {
            if (data.an) {
              const ok = await confirm({
                title: 'Forum abschalten?',
                message:
                  'Der Menüpunkt verschwindet für alle, und laufende Gespräche brechen mitten ' +
                  'im Satz ab. Gelöscht wird nichts.',
                confirmLabel: 'Abschalten',
                variant: 'danger',
              });
              if (!ok) return;
            }
            schalten.mutate(!data.an);
          }}
          className={`shrink-0 rounded-md px-4 py-2 text-sm font-medium text-white ${
            data.an ? 'bg-slate-600 hover:bg-slate-700' : 'bg-green-600 hover:bg-green-700'
          }`}
        >
          {data.an ? 'Abschalten' : 'Einschalten'}
        </button>
      </div>

      <dl className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          ['Bereiche', data.kategorien],
          ['Themen', data.themen],
          ['Beiträge', data.beitraege],
          ['Offene Meldungen', data.offeneMeldungen],
        ].map(([label, wert]) => (
          <div key={label as string} className="rounded-md bg-slate-50 px-3 py-2">
            <dt className="text-xs text-slate-500">{label}</dt>
            <dd
              className={`text-lg font-semibold ${
                label === 'Offene Meldungen' && (wert as number) > 0
                  ? 'text-amber-600'
                  : 'text-slate-800'
              }`}
            >
              {wert as number}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

// ─── Meldungen ───────────────────────────────────────────────────────────────

function MeldungKarte({ meldung }: { meldung: ForumMeldung }) {
  const queryClient = useQueryClient();
  const [ergebnis, setErgebnis] = useState('');
  const [sperrOffen, setSperrOffen] = useState(false);
  const [sperrTage, setSperrTage] = useState(7);
  const [sperrGrund, setSperrGrund] = useState('');

  const neuladen = () => queryClient.invalidateQueries({ queryKey: ['admin', 'forum'] });

  const ausblenden = useMutation({
    mutationFn: (an: boolean) => blendeBeitragAus(meldung.beitrag.id, an),
    onSuccess: neuladen,
    onError: (err: Error) => toast.error(err.message),
  });

  const schliessen = useMutation({
    mutationFn: () => aendereThema(meldung.beitrag.thema.id, { geschlossen: true }),
    onSuccess: () => { neuladen(); toast.success('Thema geschlossen.'); },
    onError: (err: Error) => toast.error(err.message),
  });

  const erledigen = useMutation({
    mutationFn: () => erledigeMeldung(meldung.id, ergebnis),
    onSuccess: () => { neuladen(); toast.success('Erledigt.'); },
    onError: (err: Error) => toast.error(err.message),
  });

  const sperren = useMutation({
    mutationFn: () => setzeSperre(meldung.beitrag.verfasserId!, sperrTage, sperrGrund),
    onSuccess: () => {
      setSperrOffen(false);
      setSperrGrund('');
      neuladen();
      toast.success('Schreibsperre gesetzt.');
    },
    onError: (err: Error) => toast.error(err.message || 'Die Sperre ging nicht durch.'),
  });

  return (
    <article className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <header className="flex flex-wrap items-center gap-2 text-sm">
        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">
          {GRUND_LABELS[meldung.grund] ?? meldung.grund}
        </span>
        <span className="text-slate-500">
          gemeldet von {meldung.melder} · {zeit(meldung.gemeldetAm)}
        </span>
        {meldung.erledigtAm && (
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
            erledigt {zeit(meldung.erledigtAm)}
          </span>
        )}
      </header>

      {meldung.freitext && (
        <p className="mt-2 rounded-md bg-slate-50 p-2 text-sm italic text-slate-600">
          „{meldung.freitext}"
        </p>
      )}

      <div className="mt-3 rounded-md border border-slate-200 p-3">
        <p className="text-xs text-slate-500">
          in „{meldung.beitrag.thema.titel}" · von {meldung.beitrag.verfasser} ·{' '}
          {zeit(meldung.beitrag.erstelltAm)}
          {meldung.beitrag.ausgeblendet && ' · ausgeblendet'}
          {meldung.beitrag.geloescht && ' · vom Verfasser gelöscht'}
        </p>
        {/* Als Text, nicht als Beitrag: Hier wird beurteilt, was jemand
            geschrieben hat – Formatierung würde nur ablenken, und der Betrieb
            soll sehen, was wirklich dasteht. */}
        <pre className="mt-2 whitespace-pre-wrap break-words font-sans text-sm text-slate-800">
          {meldung.beitrag.text}
        </pre>
      </div>

      {!meldung.erledigtAm && (
        <div className="mt-3 space-y-3">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => ausblenden.mutate(!meldung.beitrag.ausgeblendet)}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
            >
              <EyeOff className="h-3.5 w-3.5" aria-hidden="true" />
              {meldung.beitrag.ausgeblendet ? 'Wieder einblenden' : 'Ausblenden'}
            </button>
            {!meldung.beitrag.thema.geschlossen && (
              <button
                onClick={() => schliessen.mutate()}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
              >
                <Lock className="h-3.5 w-3.5" aria-hidden="true" />
                Thema schließen
              </button>
            )}
            {meldung.beitrag.verfasserId && (
              <button
                onClick={() => setSperrOffen(!sperrOffen)}
                className="inline-flex items-center gap-1.5 rounded-md border border-red-300 px-3 py-1.5 text-sm text-red-700 hover:bg-red-50"
              >
                <ShieldBan className="h-3.5 w-3.5" aria-hidden="true" />
                Schreibsperre
              </button>
            )}
          </div>

          {sperrOffen && (
            <div className="space-y-2 rounded-md border border-red-200 bg-red-50 p-3">
              <div className="flex flex-wrap items-center gap-2">
                <label className="text-sm text-red-900">Für</label>
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={sperrTage}
                  onChange={(e) => setSperrTage(Number(e.target.value))}
                  className="w-20 rounded-md border border-red-300 px-2 py-1 text-sm"
                />
                <span className="text-sm text-red-900">Tage</span>
              </div>
              <textarea
                value={sperrGrund}
                onChange={(e) => setSperrGrund(e.target.value)}
                rows={2}
                placeholder="Grund – steht in der Auskunft an den Betroffenen (mind. 10 Zeichen)"
                className="w-full rounded-md border border-red-300 px-2 py-1.5 text-sm"
              />
              <button
                onClick={() => sperren.mutate()}
                disabled={sperrGrund.trim().length < 10 || sperren.isPending}
                className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                Sperren
              </button>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <input
              value={ergebnis}
              onChange={(e) => setErgebnis(e.target.value)}
              placeholder="Was wurde getan? (freiwillig)"
              className="min-w-0 flex-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
            />
            <button
              onClick={() => erledigen.mutate()}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
            >
              Erledigt
            </button>
          </div>
        </div>
      )}

      {meldung.erledigtAm && meldung.ergebnis && (
        <p className="mt-2 text-sm text-slate-600">Ergebnis: {meldung.ergebnis}</p>
      )}
    </article>
  );
}

function Meldungen() {
  const [alle, setAlle] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'forum', 'meldungen', alle],
    queryFn: () => ladeMeldungen(alle),
  });

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-base font-semibold text-slate-800">
          <Flag className="h-4 w-4 text-slate-400" aria-hidden="true" />
          Meldungen
        </h2>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={alle}
            onChange={(e) => setAlle(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300"
          />
          Erledigte mitzeigen
        </label>
      </div>

      {isLoading && <p className="py-6 text-center text-sm text-slate-400">Wird geladen …</p>}
      {data?.length === 0 && !isLoading && (
        <p className="rounded-lg border border-dashed border-slate-200 px-4 py-10 text-center text-sm text-slate-400">
          Nichts offen.
        </p>
      )}
      {data?.map((m) => <MeldungKarte key={m.id} meldung={m} />)}
    </section>
  );
}

// ─── Bereiche ────────────────────────────────────────────────────────────────

function Bereiche() {
  const queryClient = useQueryClient();
  const { data } = useQuery({ queryKey: ['admin', 'forum', 'kategorien'], queryFn: ladeKategorien });
  const [neu, setNeu] = useState({ schluessel: '', name: '', beschreibung: '', reihenfolge: 0 });

  const neuladen = () => queryClient.invalidateQueries({ queryKey: ['admin', 'forum'] });

  const anlegen = useMutation({
    mutationFn: () => neueKategorie(neu),
    onSuccess: () => {
      setNeu({ schluessel: '', name: '', beschreibung: '', reihenfolge: 0 });
      neuladen();
      toast.success('Bereich angelegt.');
    },
    onError: (err: Error) => toast.error(err.message || 'Das ging nicht.'),
  });

  const stilllegen = useMutation({
    mutationFn: (v: { id: string; stillgelegt: boolean }) =>
      aendereKategorie(v.id, { stillgelegt: v.stillgelegt }),
    onSuccess: neuladen,
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <section className="space-y-3">
      <h2 className="flex items-center gap-2 text-base font-semibold text-slate-800">
        <Layers className="h-4 w-4 text-slate-400" aria-hidden="true" />
        Bereiche
      </h2>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2 font-medium">Name</th>
              <th className="px-4 py-2 font-medium">Kurzform</th>
              <th className="px-4 py-2 font-medium">Themen</th>
              <th className="px-4 py-2 font-medium" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data?.map((k) => (
              <tr key={k.id} className={k.stillgelegt ? 'text-slate-400' : ''}>
                <td className="px-4 py-2">
                  <span className="font-medium">{k.name}</span>
                  {k.beschreibung && (
                    <span className="block text-xs text-slate-500">{k.beschreibung}</span>
                  )}
                </td>
                <td className="px-4 py-2 font-mono text-xs">{k.schluessel}</td>
                <td className="px-4 py-2">{k.themen}</td>
                <td className="px-4 py-2 text-right">
                  <button
                    onClick={() => stilllegen.mutate({ id: k.id, stillgelegt: !k.stillgelegt })}
                    className="text-xs text-slate-500 hover:text-slate-700"
                  >
                    {k.stillgelegt ? 'Wieder öffnen' : 'Stilllegen'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Stilllegen statt löschen: Ein Bereich, den jemand „aufräumt", nimmt
          die Beiträge mit. Stillgelegt heißt: nichts Neues, alles Alte
          weiterhin lesbar. */}
      <p className="text-xs text-slate-400">
        Bereiche werden nicht gelöscht, sondern stillgelegt – in einem
        stillgelegten Bereich entstehen keine neuen Themen, die alten bleiben
        lesbar.
      </p>

      <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-800">Neuer Bereich</h3>
        <div className="mt-3 grid gap-2 sm:grid-cols-4">
          <input
            value={neu.name}
            onChange={(e) => setNeu({ ...neu, name: e.target.value })}
            placeholder="Name"
            className="rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
          />
          <input
            value={neu.schluessel}
            onChange={(e) => setNeu({ ...neu, schluessel: e.target.value })}
            placeholder="kurzform"
            className="rounded-md border border-slate-300 px-2.5 py-1.5 font-mono text-sm"
          />
          <input
            value={neu.beschreibung}
            onChange={(e) => setNeu({ ...neu, beschreibung: e.target.value })}
            placeholder="Beschreibung"
            className="rounded-md border border-slate-300 px-2.5 py-1.5 text-sm sm:col-span-2"
          />
        </div>
        <button
          onClick={() => anlegen.mutate()}
          disabled={neu.name.trim().length < 2 || neu.schluessel.trim().length < 2}
          className="mt-3 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          Anlegen
        </button>
      </div>
    </section>
  );
}

// ─── Sperren ─────────────────────────────────────────────────────────────────

function Sperren() {
  const queryClient = useQueryClient();
  const { data } = useQuery({ queryKey: ['admin', 'forum', 'sperren'], queryFn: ladeSperren });

  const aufheben = useMutation({
    mutationFn: (id: string) => hebeSperreAuf(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'forum'] });
      toast.success('Sperre aufgehoben.');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <section className="space-y-3">
      <h2 className="flex items-center gap-2 text-base font-semibold text-slate-800">
        <ShieldBan className="h-4 w-4 text-slate-400" aria-hidden="true" />
        Laufende Schreibsperren
      </h2>

      {data?.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-200 px-4 py-10 text-center text-sm text-slate-400">
          Keine laufenden Sperren.
        </p>
      ) : (
        <div className="space-y-2">
          {data?.map((s) => (
            <div
              key={s.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3 shadow-sm"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800">
                  {s.anzeigename} <span className="font-normal text-slate-500">({s.email})</span>
                </p>
                <p className="text-xs text-slate-500">
                  noch {tage(s.bis)} Tage · seit {zeit(s.seit)} · von {s.gesetztVon}
                </p>
                <p className="mt-1 text-sm text-slate-600">{s.grund}</p>
              </div>
              <button
                onClick={() => aufheben.mutate(s.id)}
                className="shrink-0 rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
              >
                Aufheben
              </button>
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-slate-400">
        Eine Sperre setzt der Betrieb an einer Meldung – dort steht der Beitrag
        daneben, um den es geht. Lesen bleibt trotz Sperre möglich.
      </p>
    </section>
  );
}

// ─── Seite ───────────────────────────────────────────────────────────────────

const REITER = ['meldungen', 'bereiche', 'sperren'] as const;
const REITER_LABELS: Record<string, string> = {
  meldungen: 'Meldungen',
  bereiche: 'Bereiche',
  sperren: 'Sperren',
};

export function ForumPage() {
  const [reiter, setReiter] = useState<(typeof REITER)[number]>('meldungen');
  const { data: status } = useQuery({ queryKey: ['admin', 'forum', 'status'], queryFn: ladeStatus });

  return (
    <div className="flex h-full flex-col bg-slate-50">
      <header className="flex h-14 shrink-0 items-center border-b border-slate-200 bg-white px-4 shadow-sm">
        <h1 className="text-base font-semibold text-slate-800 sm:text-lg">Forum</h1>
      </header>

      <main className="flex-1 overflow-y-auto p-4 sm:p-6">
        <div className="mx-auto max-w-4xl space-y-6">
          <Schalter />

          <div className="flex gap-1 border-b border-slate-200">
            {REITER.map((r) => (
              <button
                key={r}
                onClick={() => setReiter(r)}
                className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
                  reiter === r
                    ? 'border-blue-600 text-blue-700'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                {REITER_LABELS[r]}
                {r === 'meldungen' && (status?.offeneMeldungen ?? 0) > 0 && (
                  <span className="ml-1.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">
                    {status?.offeneMeldungen}
                  </span>
                )}
              </button>
            ))}
          </div>

          {reiter === 'meldungen' && <Meldungen />}
          {reiter === 'bereiche' && <Bereiche />}
          {reiter === 'sperren' && <Sperren />}
        </div>
      </main>
    </div>
  );
}
